<script setup lang="ts">
import { computed, nextTick, onMounted, watch } from "vue";
import {
  Handle,
  Position,
  useVueFlow,
  type Edge,
} from "@vue-flow/core";
import type { DialogueNode } from "./types/DialogueNode";
import {
  getGroupOutletWarnings,
  resolveGroupOutlets,
} from "./utils/groupOutlets";
import {
  getFlowClipDuration,
  getGroupTimelineEnd,
  getPerformanceClipDuration,
} from "./utils/groupTimeline";
import { getDialogueClips } from "./utils/dialogueClips";

const props = withDefaults(defineProps<{
  id: string;
  node: DialogueNode;
  edges?: Edge[];
  selected?: boolean;
  exportIndex?: number;
  annotation?: string;
}>(), {
  edges: () => [],
  selected: false,
  annotation: "",
});

const emit = defineEmits<{ "update:annotation": [value: string] }>();

function updateAnnotation(event: Event) {
  emit("update:annotation", (event.target as HTMLInputElement).value);
}

const { updateNodeInternals } = useVueFlow();

function toSeconds(value: unknown) {
  const seconds = Number(value);
  return Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
}

const performanceClips = computed(() =>
  props.node.lines.flatMap((line) => line.clips),
);
const dialogues = computed(() => getDialogueClips(props.node));
const dialogueSummary = computed(() => dialogues.value.map(clip =>
  `${clip.speaker ? `${clip.speaker}：` : ""}${clip.content}`,
).join(" / "));
const autoDuration = computed(() => getGroupTimelineEnd(props.node));

const duration = computed(() =>
  props.node.durationMode === "Fixed"
    ? Math.max(toSeconds(props.node.duration), autoDuration.value)
    : autoDuration.value,
);

const durationLabel = computed(() => `${duration.value.toFixed(1)}s`);
const outletState = computed(() => resolveGroupOutlets(props.node));
const outlets = computed(() => outletState.value.outlets);
const outletWarnings = computed(() =>
  getGroupOutletWarnings(props.node),
);
const outletSignature = computed(() =>
  outlets.value.map((outlet) => outlet.id).join("|"),
);

async function refreshHandles() {
  await nextTick();
  updateNodeInternals([props.id]);
}

watch(outletSignature, refreshHandles, { flush: "post" });
onMounted(refreshHandles);

const previewSegments = computed(() => {
  const visibleClips = [
    ...(props.node.select ? [props.node.select] : []),
    ...performanceClips.value,
  ].slice(0, 6);
  const total = Math.max(duration.value, 0.25);

  return visibleClips.map((clip, index) => ({
    id: `${props.id}-${index}`,
    left: `${(toSeconds(clip.startTime) / total) * 100}%`,
    width: `${
      (Math.max(
        "continueDelayTime" in clip
          ? getFlowClipDuration(props.node, clip)
          : toSeconds(getPerformanceClipDuration(clip)),
        0.25,
      ) /
        total) *
      100
    }%`,
  }));
});
</script>

<template>
  <article class="group-node" :class="{ selected }">
    <header class="group-header">
      <span v-if="exportIndex !== undefined" class="export-index" :aria-label="`TimeLineGroup 索引 ${exportIndex}`" title="导出后的 TimeLineGroup 全局索引，从 0 开始，可用于播放调试">#{{ exportIndex }}</span>
      <input class="nodrag group-kind graph-node-annotation" :value="annotation" aria-label="节点备注（仅编辑器显示，不参与导出）" :title="annotation || '点击填写节点备注，仅保存在工程中'" placeholder="普通事件节点"
        @input="updateAnnotation" @pointerdown.stop @mousedown.stop @click.stop @keydown.stop />
      <div class="group-header-meta">
        <span
          v-if="outletWarnings.length"
          class="group-warning"
          :title="outletWarnings.join('\n')"
          >⚠ {{ outletWarnings.length }}</span
        >
        <span class="group-duration">{{ durationLabel }}</span>
      </div>
    </header>

    <div class="group-flow-ports">
      <div
        v-for="(outlet, index) in outlets"
        :key="outlet.id"
        class="group-port-row"
        :class="`outlet-${outlet.kind.toLowerCase()}`"
      >
        <span class="group-input-label">
          <Handle
            v-if="index === 0"
            id="input"
            class="group-input-handle"
            type="target"
            :position="Position.Left"
            title="Group 入口"
            aria-label="Group 入口"
          />
          <template v-if="index === 0">入口</template>
        </span>
        <span class="group-output-label" :title="outlet.label">
          <span class="group-output-text">{{ outlet.label }}</span>
          <span
            v-if="node.focusPush?.outputMode === 'Shared' && node.focusPush.sharedOutletIndex === index"
            class="shared-focus-badge"
            title="Focus Push 到达触发时间后会强制使用此出口"
          >强制共用</span>
        </span>
        <Handle
          :id="outlet.id"
          class="group-output-handle"
          type="source"
          :position="Position.Right"
          :title="outlet.label"
          :aria-label="outlet.label"
        />
      </div>

      <div v-if="!outlets.length" class="group-port-row no-flow-output">
        <span class="group-input-label">
          <Handle
            id="input"
            class="group-input-handle"
            type="target"
            :position="Position.Left"
            title="Group 入口"
            aria-label="Group 入口"
          />
          入口
        </span>
        <span>⚠ 无流程出口</span>
      </div>
    </div>

    <section class="group-content">
      <div
        class="group-dialogue"
        :class="{ empty: !dialogues.length }"
        :title="dialogueSummary"
      >
        <template v-if="dialogues.length">{{ dialogueSummary }}</template>
        <template v-else>暂无 Dialogue Clip</template>
      </div>

      <div class="group-stats">
        <span v-if="dialogues.length > 1">{{ dialogues.length }} 台词</span>
        <span>{{ performanceClips.length }} 演出</span>
        <span>{{ node.select?.options.length ?? 0 }} 选项</span>
      </div>

      <div v-if="previewSegments.length" class="timeline-preview" aria-hidden="true">
        <span
          v-for="(segment, index) in previewSegments"
          :key="segment.id"
          class="timeline-segment"
          :class="`segment-${(index % 3) + 1}`"
          :style="{ left: segment.left, width: segment.width }"
        />
      </div>
    </section>
  </article>
</template>

<style scoped>
.group-node {
  --dsfg-handle-fill: #488aeb;
  --dsfg-handle-ring: #fff;
  width: 250px;
  overflow: visible;
  color: #334155;
  background: #fff;
  border: 1px solid #cbd7e6;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(38, 59, 90, 0.10);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.group-header {
  border-radius: 9px 9px 0 0;
  gap: 7px;
}

.graph-node-annotation {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 3px 5px;
  color: #315b8e;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px;
  outline: 0;
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
}
.graph-node-annotation::placeholder { color: inherit; opacity: 1; }
.graph-node-annotation:hover, .graph-node-annotation:focus { background: #fff; border-color: #b9cdeb; }
.graph-node-annotation:focus-visible { outline: 2px solid #488aeb; outline-offset: 1px; }
.export-index { flex-shrink: 0; padding: 2px 6px; border: 1px solid #b9cdeb; border-radius: 5px; color: #315d99; background: #fff; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }

.group-header-meta {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
}

.group-warning {
  color: #94651c;
  font-size: 10px;
  font-weight: 700;
}

.group-node:hover {
  border-color: #94b8e8;
}

.group-node.selected {
  border-color: #488aeb;
  box-shadow:
    0 0 0 2px rgba(72, 138, 235, 0.20),
    0 10px 28px rgba(38, 59, 90, 0.14);

}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  padding: 0 11px;
  background: #e2edfc;
}

.group-kind {
  margin-right: auto;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.group-duration {
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  opacity: 0.92;
}

.group-content {
  padding: 11px;
  border-radius: 0 0 9px 9px;
}

.group-dialogue {
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-stats {
  display: flex;
  gap: 10px;
  margin-top: 10px;
  color: #64748b;
  font-size: 11px;
}

.timeline-preview {
  position: relative;
  height: 18px;
  margin-top: 9px;
  overflow: hidden;
  background: #edf2fa;
  border-radius: 4px;
}

.timeline-segment {
  position: absolute;
  inset-block: 0;
  min-width: 5px;
  border-radius: 2px;
}

.segment-1 {
  background: #94b8e8;
}

.segment-2 {
  background: #b3a5df;
}

.segment-3 {
  background: #99cdbb;
}

.group-flow-ports {
  overflow: visible;
  background: #f6f9fd;
  border-bottom: 1px solid #dbe3ed;
}

.group-port-row {
  position: relative;
  display: grid;
  grid-template-columns: 70px minmax(0, 1fr);
  align-items: center;
  min-height: 28px;
  padding: 0 12px;
  color: #64748b;
  border-bottom: 1px solid #e5ebf3;
  font-size: 10px;
}

.group-port-row:last-child {
  border-bottom: 0;
}

.group-input-label {
  color: #71839a;
}

.group-output-label {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-output-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shared-focus-badge {
  flex-shrink: 0;
  padding: 2px 4px;
  color: #94651c;
  background: #fff8e8;
  border: 1px solid #ead7ae;
  border-radius: 4px;
  font-size: 9px;
  line-height: 1.2;
}

.group-port-row.outlet-dialogue .group-output-label {
  color: #315b8e;
}

.group-port-row.outlet-select .group-output-label {
  color: #79569e;
}

.group-port-row.outlet-focuspush .group-output-label { color: #b98438; }
.group-port-row.outlet-focuspush .group-output-handle { --dsfg-handle-fill: #b98438; }

.group-port-row.outlet-select .group-output-handle {
  --dsfg-handle-fill: #9d79c5;
}

.group-input-handle,
.group-output-handle {
  z-index: 4;
}

.no-flow-output {
  color: #94651c;
  background: #fff8e8;
}

.no-flow-output > span:last-child {
  text-align: right;
}


.group-header { color: #315b8e; border-bottom: 1px solid #cdddf1; }
.group-kind { letter-spacing: .04em; }
.group-duration { padding: 2px 6px; background: #fff9; border-radius: 4px; font-size: 11px; }
.group-dialogue { line-height: 1.65; }
.group-dialogue.empty { color: #8090a6; }
.group-stats span { padding: 2px 6px; background: #f1f5fa; border-radius: 4px; }
@media (prefers-reduced-motion: reduce) { .group-node { transition: none; } }

</style>
